import React, { useMemo, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateOrderFormField } from '../redux_files/actions/orderFormActions';
import { THEME } from '../utils/theme';
import LocationPicker from './location_picker';
import DeliveryChargeInfo from './DeliveryChargeInfo';
import { FREE_DELIVERY_RADIUS_KM, DELIVERY_CENTER } from '../utils/locationConstants';
import { calculateDistance as calculateDistanceUtil } from '../utils/deliveryUtils';

const LocationModal = ({
  showModal,
  selectedDeliveryCoords,
  onLocationSelect,
  onClose
}) => {
  // Hooks must be called unconditionally at the top of the component
  const dispatch = useDispatch();
  const [pendingSelection, setPendingSelection] = useState({ location: null, address: '' });
  const [confirmError, setConfirmError] = useState('');
  const [isLocating, setIsLocating] = useState(false);
  const { numberOfPlates = 0, plateSize = 'full' } = useSelector(state => state.orderForm || {});
  const handleSelectionChange = useCallback(({ location, address }) => {
    setPendingSelection({ location, address });
  }, []);
  const desktopPickerRef = useRef(null);
  const isMobileOrTablet = useMemo(() => {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const isUAmobile = /Mobi|Android|iPhone|iPad|iPod/i.test(ua);
    const isSmallViewport = typeof window !== 'undefined' ? window.innerWidth <= 768 : false;
    return isUAmobile || isSmallViewport;
  }, []);

  // Styles object using THEME
  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: THEME.overlay.dark,
      zIndex: 2000,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '10px'
    },
    content: {
      backgroundColor: THEME.neutral.white,
      borderRadius: '10px',
      width: '100%',
      maxWidth: '900px',
      maxHeight: '90vh',
      overflow: 'hidden',
      boxShadow: `0 10px 30px ${THEME.shadow.dark}`,
      display: 'flex',
      flexDirection: 'column'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '16px',
      borderBottom: `1px solid ${THEME.border.light}`
    },
    title: {
      fontSize: '18px',
      fontWeight: 600,
      color: THEME.text.primary
    },
    closeButton: {
      background: 'transparent',
      border: 'none',
      fontSize: '18px',
      cursor: 'pointer',
      color: THEME.text.primary
    },
    bodyContainer: (isMobile) => ({
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      flex: 1,
      minHeight: 0,
      overflowY: isMobile ? 'auto' : 'hidden'
    }),
    mobileContent: {
      padding: '16px'
    },
    leftPane: {
      flex: 2,
      padding: '16px',
      overflowY: 'auto',
      overflowX: 'hidden',
      borderRight: `1px solid ${THEME.border.light}`
    },
    rightPane: {
      flex: 1,
      padding: '16px',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      justifyContent: 'space-between'
    },
    infoText: {
      fontSize: '14px',
      color: THEME.text.secondary
    },
    selectedPanel: {
      marginTop: '8px',
      padding: '12px',
      backgroundColor: THEME.success.background,
      borderLeft: `4px solid ${THEME.success.main}`,
      borderRadius: '6px',
      fontSize: '13px',
      color: THEME.success.main
    },
    selectedAddress: {
      marginTop: '6px',
      fontSize: '12px',
      color: THEME.success.dark
    },
    errorPanel: {
      marginTop: '8px',
      padding: '12px',
      backgroundColor: THEME.error.light,
      borderLeft: `4px solid ${THEME.error.main}`,
      borderRadius: '6px',
      fontSize: '13px',
      color: THEME.error.darkText
    },
    messageText: {
      marginTop: '6px'
    },
    actions: {
      marginTop: 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px'
    },
    mobileFooter: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '10px',
      padding: '2px 15px 12px',
      borderTop: `1px solid ${THEME.border.light}`,
      backgroundColor: THEME.neutral.white
    }
  };

  if (!showModal) return null;

  // Clear confirm error when a valid selection becomes available
  if (pendingSelection.address && pendingSelection.location && confirmError) {
    setConfirmError('');
  }

  return (
    <div className='location-modal-overlay' style={styles.overlay}>
      <div
        className='location-modal-content'
        style={styles.content}
      >
        <div style={styles.header}>
          <div style={styles.title}>Select Delivery Location</div>
          <button
            type='button'
            onClick={onClose}
            style={styles.closeButton}
            aria-label='Close location selector'
          >
            ✕
          </button>
        </div>

        <div style={styles.bodyContainer(isMobileOrTablet)}>
          {isMobileOrTablet ? (
            <div style={styles.mobileContent}>
              <LocationPicker
                initialLocation={selectedDeliveryCoords}
                onLocationSelect={onLocationSelect}
                showInternalConfirmButton={false}
                onSelectionChange={handleSelectionChange}
                alignCurrentLocationRight={!isMobileOrTablet}
                onLocatingChange={setIsLocating}
              />
              {confirmError && (
                <div style={styles.errorPanel} role='alert'>
                  <strong>⚠️ Location Error:</strong>
                  <div style={styles.messageText}>{confirmError}</div>
                </div>
              )}
            </div>
          ) : (
            <>
              <div style={styles.leftPane}>
                <LocationPicker
                  ref={desktopPickerRef}
                  initialLocation={selectedDeliveryCoords}
                  onLocationSelect={onLocationSelect}
                  showInternalConfirmButton={false}
                  hideSelectedInfoPanel={true}
                  onSelectionChange={handleSelectionChange}
                  showCurrentLocationButton={false}
                  alignCurrentLocationRight={!isMobileOrTablet}
                  onLocatingChange={setIsLocating}
                />
              </div>
              <div style={styles.rightPane}>
                <div style={styles.infoText}>
                  Select a location on the map, then confirm.
                </div>
                {pendingSelection.address && (
                  <div style={styles.selectedPanel}>
                    <strong>✓ Selected Location:</strong>
                    <div style={styles.selectedAddress}>
                      {pendingSelection.address}
                    </div>
                  </div>
                )}
                {confirmError && (
                  <div style={styles.errorPanel}>
                    <strong>⚠️ Location Error:</strong>
                    <div style={styles.messageText}>Please select a location on map before confirming.</div>
                  </div>
                )}
                {pendingSelection.location && (
                  <DeliveryChargeInfo
                    deliveryDistance={calculateDistanceUtil(DELIVERY_CENTER.lat, DELIVERY_CENTER.lng, pendingSelection.location.lat, pendingSelection.location.lng)}
                    numberOfPlates={numberOfPlates}
                    plateSize={plateSize}
                    radiusKm={FREE_DELIVERY_RADIUS_KM}
                  />
                )}
                <div style={styles.actions}>
                  <button
                    type='button'
                    className='nav-button'
                    onClick={() => {
                      if (desktopPickerRef.current && desktopPickerRef.current.triggerCurrentLocation) {
                        desktopPickerRef.current.triggerCurrentLocation();
                      }
                    }}
                    disabled={isLocating}
                    aria-busy={isLocating}
                  >
                    {isLocating ? '⏳ Locating...' : '📍 Use Current Location'}
                  </button>
                  <button
                    type='button'
                    className='nav-button next-button'
                    onClick={() => {
                      if (!(pendingSelection.address && pendingSelection.location)) {
                        setConfirmError('Please select a location on map before confirming.');
                        return;
                      }
                      setConfirmError('');
                      dispatch(updateOrderFormField('selectedDeliveryCoords', pendingSelection.location));
                      onLocationSelect(pendingSelection.location, pendingSelection.address);
                    }}
                  >
                    Confirm Location
                  </button>
                  <button
                    type='button'
                    onClick={onClose}
                    className='nav-button prev-button'
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {isMobileOrTablet && (
          <div style={styles.mobileFooter}>
             <button
              type='button'
              onClick={onClose}
              className='nav-button prev-button'
            >
              Cancel
            </button>
            <button
              type='button'
              className='nav-button next-button'
              onClick={() => {
                if (!(pendingSelection.address && pendingSelection.location)) {
                  setConfirmError('Please select a location before confirming.');
                  return;
                }
                setConfirmError('');
                dispatch(updateOrderFormField('selectedDeliveryCoords', pendingSelection.location));
                onLocationSelect(pendingSelection.location, pendingSelection.address);
              }}
            >
              Confirm Location
            </button>
           
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationModal;
